<template>
  <div class="collapse navbar-collapse" id="navbarSupportedContent">
    <ul class="navbar-nav me-auto mb-2 mb-lg-0 navbar-nav--uniform">

      <!-- Home (and any other menu items) -->
      <li
        class="nav-item"
        v-for="(menu, index) in filterMenuList(menuList)"
        :key="index"
      >
        <router-link
          class="nav-link r-font fw-bolder d-hex-color"
          :to="{ name: menu?.name }"
        >
          {{ menu.title }}
        </router-link>
      </li>

      <!-- Mobile only (guests): Share → Join -->
      <li class="nav-item d-lg-none" v-if="!userData || userData?.role == 'guest'">
        <router-link class="nav-link r-font fw-bolder d-hex-color" :to="{ name: ROUTES.SHARE_YOUR_EVENT }" @click="trackEvent('share_business_event_click')">
          Share Your Business / Events
        </router-link>
      </li>
      <li class="nav-item d-lg-none" v-if="!userData || userData?.role == 'guest'">
        <router-link class="nav-link r-font fw-bolder d-hex-color" :to="{ name: ROUTES.JOIN_COMMUNITY }" @click="trackEvent('join_community_click')">
          Join the Community
        </router-link>
      </li>

      <!-- Desktop guests: Share → Join (BEFORE About Us) -->
      <li class="nav-item d-none d-lg-flex" v-if="!userData || userData?.role == 'guest'">
        <router-link class="nav-link r-font fw-bolder d-hex-color" :to="{ name: ROUTES.SHARE_YOUR_EVENT }" @click="trackEvent('share_business_event_click')">
          Share your business or event
        </router-link>
      </li>
      <li class="nav-item d-none d-lg-flex" v-if="!userData || userData?.role == 'guest'">
        <router-link class="nav-link r-font fw-bolder d-hex-color" :to="{ name: ROUTES.JOIN_COMMUNITY }" @click="trackEvent('join_community_click')">
          Join the Community
        </router-link>
      </li>

      <!-- Logged-in organiser/planner: Create an Event (BEFORE About Us) -->
      <li
        class="nav-item"
        v-if="userData && (userData?.role === ROLES.ORGANIZER || userData?.role === ROLES.PLANNER)"
      >
        <router-link class="create-event-btn" :to="{ name: ROUTES.ADD_EVENTS }">
          Create an Event
        </router-link>
      </li>

      <!-- About Us -->
      <li class="nav-item">
        <router-link class="nav-link r-font fw-bolder d-hex-color" :to="{ name: ROUTES.ABOUT_US }">
          About Us
        </router-link>
      </li>

      <!-- What's New dropdown -->
      <li v-if="shouldShowWhatsNew" class="nav-item position-relative" @mouseenter="openWhatsNew" @mouseleave="closeWhatsNew">
        <button class="nav-link r-font fw-bolder d-hex-color nav-dropdown-toggler">
          What's New
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" class="nav-dropdown-caret">
            <path d="M15.3 9.3a1 1 0 0 1 1.4 1.4l-4 4a1 1 0 0 1-1.4 0l-4-4a1 1 0 0 1 1.4-1.4l3.3 3.29 3.3-3.3z"></path>
          </svg>
        </button>
        <transition name="nav-dropdown-anim">
          <div v-if="isWhatsNewOpen" class="nav-dropdown-panel">
            <router-link
              v-for="page in whatsNewPages"
              :key="page._id"
              class="nav-dropdown-item"
              :to="{ name: ROUTES.DYNAMIC_CONTENT_PAGE_SLUG, params: { slug: page.slug } }"
              @click="closeWhatsNew"
            >{{ page.title }}</router-link>
          </div>
        </transition>
      </li>

      <!-- Search icon — all users -->
      <li class="nav-item">
        <div class="search">
          <form>
            <router-link class="search-icon" :to="{ name: ROUTES.SEARCH }" @click="trackEvent('search_click', { location: 'navbar' })">
              <img :src="search" alt="img" />
            </router-link>
          </form>
        </div>
      </li>

    </ul>
    <!-- position-relative creates the containing block for the absolute dropdown panel -->
    <div class="position-relative" v-if="userData?.role != 'guest' && userData">
      <logged-in-user-nav-dropdown
        @user-logged-out="userLoggedOut"
        :user-id="userId"
        :logged-in-user-role="userData?.role"
      />
    </div>
  </div>

  <!-- Guests: Login / Sign Up (rightmost) -->
  <div class="g-btn" v-if="!userData || userData?.role == 'guest'">
    <router-link class="nav-login-btn" :to="{ name: ROUTES.LOGIN }" @click="trackEvent('login_click')">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z" fill="currentColor"/>
      </svg>
      <span>Login / Sign Up</span>
    </router-link>
  </div>

  <span id="updateHeader" @click="updateHeader()"></span>
  <!--List Of All Auth Modals-->
  <auth-modals
    :open-login="isOpenLoginModal"
    :emailVerificationReq="true"
    :openSignup="openSignup"
    :close="closeModal"
    :role="role"
  />
</template>

<script setup>
import search from "@/assets/header/search.svg";
import { onMounted, ref, watch, computed } from "vue";
import { ROUTES } from "@/utils/constants/routes";
import TokenService from "@/services/token.service";
import { useRoute, useRouter } from "vue-router";
import LoggedInUserNavDropdown from "@/components/presentational/home/loggedin-user-nav-dropdown";
import AuthModals from "@/components/presentational/auth/auth-modals";
import { ROLES } from "@/utils/constants";
import useUrlQuery from "@/composables/use-url-query";
import useToaster from "@/composables/use-toaster";
import tokenService from "@/services/token.service";
import ApiClient from "@/methods/apiclient";

const userData = ref(TokenService.getUser());
const role = ref("");
const openSignup = ref("");
const emailVerificationUserId = useUrlQuery("email-verification") || "";
const whatsNewPages = ref([]);
const isWhatsNewOpen = ref(false);

if (emailVerificationUserId) {
  // useToaster('success', '', 'Email Verified Successfully')
  // router.push({
  //   name: ROUTES.EMAIL_VERIFICATION, query: {
  //     email: response.value.data?.data?.user?.email
  //   }
  // })
}

const route = useRoute();
const router = useRouter();
const cartData = ref(TokenService.getCartData());

// {
//     title: "Cultures",
//     name: ROUTES.CULTURE,
//   },
const menuList = ref([
  {
    title: "Home",
    name: ROUTES.HOME,
  },
  // {
  //   title: "Browse Events",
  //   name: ROUTES.BROWSE_EVENTS,
  // },
  // {
  //   title: "Explore the City",
  //   name: ROUTES.WHAT_IN_THE_CITY,
  // },
  // {
  //   title: "Become an Ambassador",
  //   name: ROUTES.BECOMEAMBASSADOR,
  // },
]);

const menuOpen = ref(false);
const filterMenuList = (menu) => {
  if (
    (userData.value?.role !== ROLES.ORGANIZER &&
      userData.value?.role !== ROLES.PLANNER) ||
    !userData.value
  ) {
    return menu.filter((link) => link.name !== ROUTES.ADD_EVENTS);
  } else return menu;
};
const userId = ref(TokenService.getUser()?._id);

const article = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "Culture In Your City",
  publisher: {
    "@type": "CreativeWork",
    name: "Culture In Your City",
  },
};

const existingScripts = Array.from(
  document.querySelectorAll('script[type="application/ld+json"]'),
);
existingScripts.forEach((script) => {
  script.parentNode.removeChild(script);
});

const script = document.createElement("script");
script.type = "application/ld+json";
script.textContent = JSON.stringify(article);
document.head.appendChild(script);

//Logout
const guestLogout = () => {
  // localStorage.clear()
  tokenService.clearStorage();
  userData.value = "";
  router.push({ path: "/" });
  useToaster("success", "", "Logout Successfully.");
};

//Listening to route changes to check if user is still logged in
watch(
  () => route.path,
  () => {
    cartData.value = TokenService.getCartData();
    userData.value = TokenService.getUser();
    mobileMenuDropdown(false);
    isWhatsNewOpen.value = false;
  },
);

const updateHeader = () => {
  let el = document.getElementById("loginupdate");
  if (el) el.click();
  userData.value = TokenService.getUser();
  cartData.value = TokenService.getCartData();
  console.log("updateHeader");
};

//After user is logged out
const userLoggedOut = () => {
  // localStorage.clear()
  tokenService.clearStorage();
  userData.value = "";
  router.push({ path: "/" });
};

const closeModal = () => {
  openSignup.value = "";
  console.log("closeModal");
};

const isOpenLoginModal = ref(false);
const openLogin = (p, r = "") => {
  if (localStorage.getItem("isPurchase")) {
    localStorage.removeItem("isPurchase");
    // localStorage.removeItem('cartData')
  }

  role.value = r;

  isOpenLoginModal.value = !isOpenLoginModal.value;
  document.getElementById("body").classList.add("modalOpened");
};

const openSignupModal = (r = "") => {
  role.value = r;
  openSignup.value = r;
  // router.push({path:'/?auth-page=organizer-signup'})
  document.getElementById("body").classList.add("modalOpened");
};

const mobileMenuDropdown = (show = true) => {
  const el = document.getElementById("dropdown-menu-ul");
  if (!el) return;
  if (el.classList.contains("show")) {
    el.classList.remove("show");
  } else if (show === true) {
    el.classList.add("show");
  }
};

const loginLink = useUrlQuery("login-link");

if (loginLink != "" && loginLink != undefined) {
  var payload = {
    code: loginLink,
  };

  ApiClient.get("auth/login/link", payload).then((res) => {
    if (res.data) {
      router.push({ name: ROUTES.LOGIN_AS_USER, query: { id: res.data.id } });
    } else {
      // useToaster("error","",res.message,4000,'red')

      if (typeof res.message == "string") {
        useToaster("danger", "", res.message);
      } else {
        useToaster("danger", "", res.message[0]);
      }
    }
  });
}

const fetchWhatsNewPages = async () => {
  try {
    const res = await ApiClient.get("static-pages/public");

    const pages = Array.isArray(res?.data)
      ? res.data
      : Array.isArray(res)
      ? res
      : [];

    // Only keep pages that are NOT hidden
    whatsNewPages.value = pages.filter(
      (page) => page && page.isHidden !== true,
    );
  } catch (err) {
    whatsNewPages.value = [];
  }
};

const shouldShowWhatsNew = computed(() => {
  return Array.isArray(whatsNewPages.value) && whatsNewPages.value.length > 0;
});

const openWhatsNew = () => {
  isWhatsNewOpen.value = true;
};
const closeWhatsNew = () => {
  isWhatsNewOpen.value = false;
};

onMounted(() => {
  fetchWhatsNewPages();
});

const authPage = useUrlQuery("auth-page");

if (authPage == "organizer-signup") {
  setTimeout(() => {
    openSignupModal("Organizer");
  }, 100);
}
if (authPage == "seeker-signup") {
  setTimeout(() => {
    openSignupModal("Seeker");
  }, 100);
}

if (authPage == "seeker-login") {
  setTimeout(() => {
    // openLogin(false, 'Seeker')
  }, 100);
}

if (authPage == "seeker-login-popup") {
  setTimeout(() => {
    openLogin(false, "Seeker");
  }, 100);
}

if (authPage == "login-popup") {
  setTimeout(() => {
    openLogin(false, "Seeker");
  }, 100);
}

if (authPage == "eventplanner-login") {
  setTimeout(() => {
    openLogin(false, "Event Planner");
  }, 100);
}
if (authPage == "organizer-login") {
  setTimeout(() => {
    // openLogin(false, 'Organizer')
  }, 100);
}

document.getElementById("body").classList.remove("modalOpened");

const trackEvent = (eventName, params = {}) => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', eventName, params);
  }
};
</script>

<style>
.origin-top-right.absolute.z-50.right-0.mt-2.w-48.bg-white.rounded-lg.shadow-primary__shadow.py-2 {
  background: #3b5998;
}

/* Override global index.css: fix dropdown anchor (right-align, not left) and
   restore the original min-width so "Manage Account/Events" fits on one line */
.header-section .menu-baar .origin-top-right {
  left: auto !important;
  right: 0 !important;
  min-width: 200px !important;
}

body .header-menu ul.dropdown-menu button {
  color: white;
}

#mobile-menu-dropdown ul li div {
  background: #3b5998 !important;
  text-align: left !important;
  justify-content: start;
  padding-left: 6px;
  padding-bottom: 5px;
  padding-top: 5px;
  color: white !important;
}
</style>

<style scoped>
li {
  white-space: nowrap;
}

/* Uniform gap across all nav items — overrides Bootstrap's nav-link horizontal padding */
.navbar-nav--uniform {
  gap: 20px;
  align-items: center;
}

.navbar-nav--uniform .nav-link {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

.navbar-nav--uniform .search {
  display: flex;
  align-items: center;
}

.organizeDropdown .dropdown-menu {
  left: initial !important;
  right: 0 !important;
}

.organizeDropdown:hover .dropdown-menu {
  display: block !important;
}

.router-link-exact-active {
  color: #e07a5f;
}

.modal-animation-enter-active,
.modal-animation-leave-active {
  transition: opacity 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.02);
}

.modal-animation-enter-from,
.modal-animation-leave-to {
  opacity: 0;
}

.modal-animation-inner-enter-active {
  transition: all 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.02) 0.15s;
}

.modal-animation-inner-leave-active {
  transition: all 0.3s cubic-bezier(0.52, 0.02, 0.19, 1.02);
}

.modal-animation-inner-enter-from {
  opacity: 0;
  transform: scale(0.8);
}

.modal-animation-inner-leave-to {
  transform: scale(0.8);
}

.hamburger__line,
.hamburger__middle {
  display: block;
  width: 30px;
  height: 2px;
  border-radius: 2px;
  background-color: #ffffff;
  margin-top: 7px;
  margin-bottom: 7px;
}

.event-none {
  pointer-events: none;
}

.hamburger__middle {
  width: 20px;
  margin-left: 10px;
}

/*--8-5-2023--*/
.header-menu a:hover {
  color: #3b5998 !important;
}

.nav-dropdown-toggler {
  display: inline-flex;
  align-items: center;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: inherit;
}

.nav-dropdown-caret {
  margin-left: 2px;
  height: 20px;
  width: 20px;
  fill: currentColor;
}

/* Dropdown panel — primary bg, white text */
.nav-dropdown-panel {
  position: absolute;
  top: 100%;
  left: 0;
  min-width: 200px;
  background: #f05537;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.13);
  z-index: 1000;
  padding: 6px 0;
  margin-top: 5px;
}

.nav-dropdown-item {
  display: block;
  padding: 8px 16px;
  font-size: 14px;
  color: #fff;
  text-decoration: none;
  transition: all 0.2s ease;
}

.nav-dropdown-item:hover {
  background: rgba(255, 255, 255, 0.2) !important;
  color: #fff !important;
}

.nav-dropdown-anim-enter-active,
.nav-dropdown-anim-leave-active {
  transition: all 0.2s ease;
}

.nav-dropdown-anim-enter-from,
.nav-dropdown-anim-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* Create an Event prominent button */
.create-event-btn {
  display: inline-flex;
  align-items: center;
  background: #f05537;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 6px;
  text-decoration: none;
  white-space: nowrap;
  transition: all 0.5s;
  font-family: inherit;
  border: 1.5px solid #f05537;
}

.create-event-btn:hover {
  background: #fff;
  color: #f05537;
}

/* Navbar CTA buttons */
.nav-cta-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 14px;
  font-weight: 600;
  padding: 8px 14px;
  border-radius: 6px;
  text-decoration: none;
  white-space: nowrap;
  transition: all 0.3s;
  font-family: inherit;
}

.nav-cta-btn--outline,
.nav-cta-btn--solid {
  background: #f05537;
  color: #fff;
  border: 1.5px solid #f05537;
}

.nav-cta-btn--outline:hover,
.nav-cta-btn--solid:hover,
.nav-cta-btn.router-link-active {
  background: #fff;
  color: #f05537 !important;
  border-color: #f05537;
}

/* Login / Sign Up button — outlined with icon + chevron */
.nav-login-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 14px;
  font-weight: 600;
  padding: 8px 14px;
  border-radius: 6px;
  text-decoration: none;
  white-space: nowrap;
  transition: all 0.3s;
  font-family: inherit;
  border: 1.5px solid #f05537;
  color: #fff;
  background: #f05537;
}

.nav-login-btn:hover {
  background: #fff;
  color: #f05537 !important;
  border-color: #f05537;
}

.nav-login-btn:hover svg,
.nav-login-btn:hover svg path {
  fill: #f05537 !important;
}

.nav-login-btn svg {
  transition: fill 0.3s;
}
</style>
